import React from 'react';
import { Stack, Typography, Box, Chip, Button } from '@mui/joy';
import { useNavigate } from 'react-router-dom';
import { Edit, Face } from '@mui/icons-material';
interface PositionCardProps {
  position: {
    id: string;
    employerId: string;
    positionId: string;
    displayNameOverride: string;
    slugOverride: string;
    description: string;
    isActive: boolean;
    visibility: 'public' | 'unlisted' | 'inactive';
    class: string | null;
    wageType: string | null;
    minWageCents: number | null;
    maxWageCents: number | null;
    isPartTime: boolean;
    isFullTime: boolean;
    isHourly: boolean;
    isSalary: boolean;
    minHourlyCents: number | null;
    maxHourlyCents: number | null;
    minSalaryCents: number | null;
    maxSalaryCents: number | null;
    receivesTips: boolean;
    mediaId: string | null;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    employer: {
      uuid: string;
      companyName: string;
      slug: string;
      address: string;
    },
    position: {
      id: string;
      displayName: string;
      slug: string;
    };
  };
}

const PositionCard: React.FC<PositionCardProps> = ({ position }) => {
  const navigate = useNavigate();

  const handleApplicantView = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    navigate(`/apply/${position.employer.slug}/${position.slugOverride}`);
  };

  const handleEmployerView = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    navigate(`/hiring/${position.employer.slug}/${position.slugOverride}`);
  };

  const handleEditPosition = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    navigate(`/hiring/${position.employer.slug}/${position.slugOverride}/edit`);
  };

  const getVisibilityColor = (visibility: 'public' | 'unlisted' | 'inactive') => {
    switch (visibility) {
      case 'public':
        return 'success';
      case 'unlisted':
        return 'warning';
      case 'inactive':
        return 'neutral';
      default:
        return 'neutral';
    }
  };

  const getVisibilityLabel = (visibility: 'public' | 'unlisted' | 'inactive') => {
    switch (visibility) {
      case 'public':
        return 'Public';
      case 'unlisted':
        return 'Unlisted';
      case 'inactive':
        return 'Inactive';
      default:
        return 'Unknown';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getEmploymentType = (isPartTime: boolean, isFullTime: boolean) => {
    if (isPartTime && isFullTime) {
      return 'Full-time or Part-time';
    } else if (isFullTime) {
      return 'Full-time';
    } else if (isPartTime) {
      return 'Part-time';
    }
    return 'Employment type not specified';
  };

  const getWageType = (isHourly: boolean, isSalary: boolean) => {
    const types = [];
    
    if (isHourly) types.push('Hourly');
    if (isSalary) types.push('Salary');
    
    return types.length > 0 ? types.join(' • ') : 'Wage type not specified';
  };

  const formatWage = (isHourly: boolean, isSalary: boolean, minHourlyCents: number | null, maxHourlyCents: number | null, minSalaryCents: number | null, maxSalaryCents: number | null, receivesTips: boolean) => {
    const formatCents = (cents: number) => {
      return `$${(cents / 100).toFixed(2)}`;
    };
    
    let wageDisplay = '';
    
    if (isHourly && (minHourlyCents || maxHourlyCents)) {
      const suffix = '/hr';
      if (minHourlyCents && maxHourlyCents) {
        wageDisplay = `${formatCents(minHourlyCents)} to ${formatCents(maxHourlyCents)}${suffix}`;
      } else if (minHourlyCents) {
        wageDisplay = `${formatCents(minHourlyCents)}+${suffix}`;
      } else if (maxHourlyCents) {
        wageDisplay = `Up to ${formatCents(maxHourlyCents)}${suffix}`;
      }
    }
    
    if (isSalary && (minSalaryCents || maxSalaryCents)) {
      const suffix = '/yr';
      if (minSalaryCents && maxSalaryCents) {
        wageDisplay = `${formatCents(minSalaryCents)} to ${formatCents(maxSalaryCents)}${suffix}`;
      } else if (minSalaryCents) {
        wageDisplay = `${formatCents(minSalaryCents)}+${suffix}`;
      } else if (maxSalaryCents) {
        wageDisplay = `Up to ${formatCents(maxSalaryCents)}${suffix}`;
      }
    }
    
    // Add tips to the wage display if applicable
    if (receivesTips) {
      if (wageDisplay) {
        wageDisplay += ' + Tips';
      } else {
        wageDisplay = 'Tips';
      }
    }
    
    return wageDisplay || null;
  };

  const employmentType = getEmploymentType(position.isPartTime, position.isFullTime);
  const wageType = getWageType(position.isHourly, position.isSalary);
  const wageDisplay = formatWage(
    position.isHourly, 
    position.isSalary, 
    position.minHourlyCents, 
    position.maxHourlyCents, 
    position.minSalaryCents, 
    position.maxSalaryCents,
    position.receivesTips
  );

  return (
    <Box
      onClick={handleEmployerView}
      sx={{
        padding: '24px',
        cursor: 'pointer',
        borderRadius: '12px',
        backgroundColor: '#F8F9FA',
        border: '2px solid #E5E7EB',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: '#2e2d7e',
          backgroundColor: '#F0F2F5',
        }
      }}
    >
      <Stack spacing={3}>
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 1 }}>
                <Typography 
                  level="h3"
                  sx={{ 
                    fontSize: '24px',
                    fontWeight: 600,
                    color: '#1A1B1D'
                  }}
                >
                  {position.displayNameOverride}
                </Typography>
                <Chip 
                  color={getVisibilityColor(position.visibility) as any}
                  size="sm"
                  variant="soft"
                >
                  {getVisibilityLabel(position.visibility)}
                </Chip>
              </Stack>
              <Typography 
                level="body-lg"
                sx={{ 
                  fontSize: '18px',
                  fontWeight: 500,
                  color: '#2e2d7e',
                  mb: 1
                }}
              >
                {employmentType} • {wageType} {wageDisplay && `• ${wageDisplay}`}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Typography 
          level="body-sm"
          sx={{ 
            fontSize: '16px',
            color: '#7B7F85',
            lineHeight: 1.4
          }}
        >
          {position.description.length > 150 
            ? `${position.description.substring(0, 150)}...` 
            : position.description
          }
        </Typography>

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography 
            level="body-sm" 
            sx={{ 
              color: '#7B7F85', 
              fontSize: '14px',
              fontWeight: 400
            }}
          >
            Posted {formatDate(position.createdAt)}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="plain"
            size="sm"
            onClick={handleApplicantView}
            sx={{
              borderRadius: "8px",
              px: 2,
              py: 0.5,
              fontSize: "0.875rem",
              fontWeight: 600,
              "&:hover": {
                borderColor: "#1a1a5e",
                color: "#1a1a5e",
                bgcolor: "rgba(46, 45, 126, 0.04)",
              },
            }}
          >
            People View
          </Button>
          
          <Button
            variant="outlined"
            size="sm"
            onClick={handleEditPosition}
            sx={{
              borderColor: "#2E2D7E",
              color: "#2E2D7E",
              borderRadius: "8px",
              px: 3,
              py: 1,
              fontSize: "0.875rem",
              fontWeight: 600,
              "&:hover": {
                borderColor: "#1a1a5e",
                color: "#1a1a5e",
                bgcolor: "rgba(46, 45, 126, 0.04)",
              },
            }}
          >
            Edit Post
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default PositionCard; 